import Konva from 'konva';
import { isDirectionalEffect } from './area_effect_geometry.js';

// A single shape preserves the existing layer styling, fog and selection behavior.
// Its geometry stays axis-aligned; line effects also show their exact direction.
export class GridAreaEffectShape extends Konva.Shape {
  getSelfRect() {
    const { bounds, size } = this.getAttr('tileFootprint');
    const x = Math.min(bounds.x, -size / 2);
    const y = Math.min(bounds.y, -size / 2);
    return {
      x,
      y,
      width: Math.max(bounds.x + bounds.width, size / 2) - x,
      height: Math.max(bounds.y + bounds.height, size / 2) - y
    };
  }

  _sceneFunc(context) {
    const { cells, size, start, end } = this.getAttr('tileFootprint');
    const occupied = new Set(cells.map(({ column, row }) => `${column},${row}`));
    context.beginPath();
    for (const { column, row } of cells) context.rect((column - 0.5) * size, (row - 0.5) * size, size, size);
    context.fillShape(this);

    // Outline only exposed edges; shared edges must not become double borders.
    context.beginPath();
    for (const { column, row } of cells) {
      const x = (column - 0.5) * size;
      const y = (row - 0.5) * size;
      const edges = [
        [0, -1, x, y, x + size, y],
        [1, 0, x + size, y, x + size, y + size],
        [0, 1, x + size, y + size, x, y + size],
        [-1, 0, x, y + size, x, y]
      ];
      for (const [dc, dr, x1, y1, x2, y2] of edges) {
        if (occupied.has(`${column + dc},${row + dr}`)) continue;
        context.moveTo(x1, y1);
        context.lineTo(x2, y2);
      }
    }
    context.strokeShape(this);

    this._drawSourceMarker(context);
    if (!this.getAttr('showDirectionGuide')) return;

    // Build the path in map coordinates, then stroke in screen pixels (as Konva
    // does for strokeScaleEnabled: false). Selection dashes never affect this line.
    context.save();
    context.beginPath();
    context.moveTo(start.x, start.y);
    context.lineTo(end.x, end.y);
    strokeInScreenPixels(context, this.stroke(), 3);
    context.restore();
  }

  _drawSourceMarker(context) {
    const { size, start } = this.getAttr('tileFootprint');
    const selected = this.getAttr('sourceSelected');
    context.save();
    context.beginPath();
    context.rect(-size / 2, -size / 2, size, size);
    context.setAttr('fillStyle', selected ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.2)');
    context.fill();
    strokeInScreenPixels(context, selected ? 'white' : 'rgba(255,255,255,0.5)', selected ? 2 : 1);
    context.restore();

    // The tile follows map scale, while the arrow stays legible without growing
    // beyond its tile or becoming oversized at high zoom.
    const zoom = Math.abs(this.getAbsoluteScale().x) || 1;
    const length = Math.min(32 / zoom, size * 0.65);
    const head = Math.min(8 / zoom, length / 3);
    const arrowColor = selected ? 'white' : 'rgba(255,255,255,0.75)';
    context.save();
    context.rotate(Math.atan2(start.y, start.x));
    context.beginPath();
    context.moveTo(length / 2, 0);
    context.lineTo(length / 2 - head, -head / 2);
    context.lineTo(length / 2 - head, head / 2);
    context.closePath();
    context.setAttr('fillStyle', arrowColor);
    context.fill();
    context.beginPath();
    context.moveTo(-length / 2, 0);
    context.lineTo(length / 2 - head / 2, 0);
    strokeInScreenPixels(context, arrowColor, 2);
    context.restore();
  }
}

export class RadiusAreaEffectShape extends Konva.Rect {
  _sceneFunc(context) {
    super._sceneFunc(context);
    const halfSpan = this.getAttr('sourceGridSize') / 4;
    const x = this.width() / 2;
    const y = this.height() / 2;
    context.beginPath();
    context.moveTo(x - halfSpan, y);
    context.lineTo(x + halfSpan, y);
    context.moveTo(x, y - halfSpan);
    context.lineTo(x, y + halfSpan);
    context.strokeShape(this);
  }
}

function strokeInScreenPixels(context, color, width) {
  const ratio = context.getCanvas().getPixelRatio();
  context.setTransform(ratio, 0, 0, ratio, 0, 0);
  context.setLineDash([]);
  context.setAttr('lineWidth', width);
  context.setAttr('lineCap', 'butt');
  context.setAttr('strokeStyle', color);
  context.stroke();
}

export function createAreaEffectShape(effect, attrs) {
  if (isDirectionalEffect(effect))
    return new GridAreaEffectShape({ ...attrs, showDirectionGuide: effect.effectType === 'line' });
  return effect.effectType === 'radius' ? new RadiusAreaEffectShape(attrs) : new Konva.Rect(attrs);
}
